import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { ScopePicker } from "@/components/scope-picker/scope-picker";
import { isScopeAvailable, pickDefaultScope } from "@/components/scope-picker/scope-sanity";
import { useScopeTree } from "@/components/scope-picker/use-scope-tree";
import { useStrategScope } from "@/features/strateg-cenowy/use-strateg-scope";
import { cn } from "@/lib/utils";

// Trasy TOP i Lider Franczyza nie operuja na per-apteka danych — cennik jest
// generowany organization-wide (jeden raport IQVIA per organizacja, konfiguracja
// w per-org schema bez idfirm). ScopePicker tu mylil: udawal ze zmiana apteki
// cos zmienia, a backend i tak ignoruje scope dla tych endpointow.
const ORG_WIDE_ROUTE_PREFIXES = [
	"/strateg-cenowy/wyniki-top",
	"/strateg-cenowy/konfiguracja-top",
	"/strateg-cenowy/lider-franczyza",
];

export const Route = createFileRoute("/_auth/strateg-cenowy")({
	component: StrategCenowyLayout,
});

function StrategCenowyLayout() {
	const { scope, setScope, persistKey } = useStrategScope();
	const { data: tree, isLoading: treeLoading } = useScopeTree();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isOrgWideRoute = ORG_WIDE_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));

	// Sanitize: konta bez aptek wlasnych (franczyzobiorca, super_admin po
	// przepieciu do org bez wlasne) nie moga uderzac w ?scope=wlasne — backend
	// zwraca 403 i podstrona crashuje przez ApiError. Patrz Sentry
	// FARMASTER-FRONTEND-24. Layout czeka na tree, przepina scope na pierwszy
	// dostepny i dopiero potem montuje children. Dla tras zbiorczych scope nie jest
	// brany pod uwage, wiec skipujemy guard.
	useEffect(() => {
		if (isOrgWideRoute) return;
		if (!tree) return;
		if (isScopeAvailable(scope, tree)) return;
		const safe = pickDefaultScope(tree);
		if (safe) setScope(safe);
	}, [tree, scope, setScope, isOrgWideRoute]);

	const scopeReady = isOrgWideRoute || (!!tree && isScopeAvailable(scope, tree));

	return (
		<div className="flex flex-col min-h-full md:h-full">
			{/* Telefon: nazwa modulu jest w pasku gornym; trasy zbiorcze nie maja
			    tu nic poza tytulem, wiec caly pas znika ponizej md. */}
			<header
				className={cn(
					"flex flex-wrap items-center justify-between gap-2 p-3 md:p-4 border-b max-md:py-2",
					isOrgWideRoute && "max-md:hidden",
				)}
			>
				<h1 className="text-lg md:text-xl font-semibold max-md:hidden">Strateg Cenowy</h1>
				{/* Slot na akcje kontekstowe podstrony (np. wskaźnik własnej konfiguracji
				    apteki z kalkulatora marż) — podstrona wstrzykuje treść portalem, żeby
				    użyć pustego pasa nagłówka zamiast tworzyć nowy wiersz nad tabelą.
				    Telefon: wybór apteki pierwszy na całą szerokość, akcje za nim
				    (gdy się nie mieszczą, przechodzą do drugiej linii). */}
				<div
					id="strateg-cenowy-header-slot"
					className="flex items-center gap-1 ml-auto max-md:order-last max-md:gap-1.5"
				/>
				{!isOrgWideRoute && (
					<ScopePicker
						value={scope}
						onChange={setScope}
						persistKey={persistKey}
						className="max-md:h-9 max-md:flex-1"
					/>
				)}
			</header>
			<main className="flex-1 md:min-h-0">
				{scopeReady ? (
					<Outlet />
				) : (
					<div className="flex items-center justify-center py-16 text-muted-foreground">
						{treeLoading || !tree ? (
							<>
								<Loader2 className="size-5 animate-spin mr-2" />
								Wczytywanie zakresu...
							</>
						) : pickDefaultScope(tree) === null ? (
							<span>Brak dostepnego zakresu dla tego konta.</span>
						) : (
							<>
								<Loader2 className="size-5 animate-spin mr-2" />
								Dostosowywanie zakresu...
							</>
						)}
					</div>
				)}
			</main>
		</div>
	);
}
